/**
 * Page /mes-referendums — espace administré « Consultations » (migration 117).
 *
 * L'habitant vote aux consultations ouvertes (1 vote, modifiable 1 h). Les
 * résultats restent masqués jusqu'à la clôture (secret du scrutin). Une
 * consultation 'indicatif' affiche un bandeau d'avertissement ; une 'officiel'
 * (FranceConnect) afficherait le bouton d'authentification.
 *
 * Accès citoyen (Règle 1) : tout rôle connecté peut voter.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/lib/auth-context';
import { api, ApiException } from '@/lib/api';
import type { Referendum } from '@/lib/api';
import { Card, Button, Badge, Alert, SectionEyebrow } from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { IdeesSection } from '@/components/administre/IdeesSection';
import {
  Vote, CheckCircle2, Lock, AlertTriangle, ShieldCheck, AlertCircle,
} from 'lucide-react';

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
  });
}

export function MesReferendumsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<Referendum[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Référendum sur lequel l'identité FC de l'usager a déjà voté (via un autre
  // compte) → on affiche un état bloqué clair au lieu de reproposer la vérif.
  // Persisté en sessionStorage pour survivre aux allers-retours dans l'app :
  // le paramètre ?dejaVote=1 n'apparaît qu'au retour FC (éphémère), or l'usager
  // peut quitter puis revenir sur la page. La sécurité réelle reste côté backend
  // (le vote est refusé quoi qu'il arrive) ; ceci ne fait qu'assurer un message
  // clair et stable. Set = ensemble des referendumId bloqués pour cette session.
  const BLOQUE_KEY = 'vpc.referendum.fc.dejaVote';
  const lireBloques = (): Set<string> => {
    try {
      const raw = sessionStorage.getItem(BLOQUE_KEY);
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch { return new Set(); }
  };
  const [bloques, setBloques] = useState<Set<string>>(() => lireBloques());
  const marquerBloque = useCallback((refId: string) => {
    setBloques((prev) => {
      const next = new Set(prev); next.add(refId);
      try { sessionStorage.setItem(BLOQUE_KEY, JSON.stringify([...next])); } catch { /* stockage indispo : on garde au moins l'état mémoire */ }
      return next;
    });
  }, []);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.referendumsCitoyen.list();
      setItems(res.referendums);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  // Retour de FranceConnect (finalité référendum) : informe l'usager clairement.
  // Lecture de l'URL UNE SEULE FOIS au montage (ref anti-double-exécution), pour
  // que le message ne dépende pas de reload ni d'un éventuel double-render.
  const fcTraite = useRef(false);
  useEffect(() => {
    if (fcTraite.current) return;
    const p = new URLSearchParams(window.location.search);
    if (p.get('fc') === 'ok' && p.get('fcVerifie') === '1') {
      fcTraite.current = true;
      const dejaVote = p.get('dejaVote') === '1';
      const refId = p.get('ref');
      if (dejaVote && refId) marquerBloque(refId);
      // Nettoie l'URL (retire les paramètres FC) sans recharger la page.
      window.history.replaceState({}, '', window.location.pathname);
      // Pose le message immédiatement (indépendant du reload).
      setNotice(
        dejaVote
          ? {
              type: 'error',
              text: 'Cette identité FranceConnect a déjà participé à cette consultation. Un seul vote par personne est autorisé.',
            }
          : {
              type: 'success',
              text: 'Identité vérifiée. Vous pouvez maintenant voter.',
            }
      );
      // Recharge la liste pour refléter l'état à jour (sans toucher au message).
      void reload();
      // Ramène l'usager sur la zone des consultations à voter.
      setTimeout(() => {
        document.getElementById('open-title')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [reload, marquerBloque]);
  const greeting = user?.prenom ? `Bonjour ${user.prenom}` : null;
  const ouvertes = items.filter((r) => r.statut === 'open');
  const closes = items.filter((r) => r.statut === 'closed');

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Consultations" accent="grc" />

      <main id="main-content" className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10 animate-fade-in">
        <SectionEyebrow as="div">Votre avis compte</SectionEyebrow>
        <h1 className="mt-1.5 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
          {greeting ? `${greeting}, voici les consultations` : 'Consultations de la commune'}
        </h1>
        <p className="mt-2 text-sm text-ink-muted">
          Votez aux consultations ouvertes. Un seul vote par personne. Les résultats sont publiés à la clôture.
        </p>

        {error && <Alert variant="error" icon={AlertCircle} className="mt-4">{error}</Alert>}
        {notice && (
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="mt-4 block w-full text-left"
            aria-label="Fermer le message"
          >
            <Alert
              variant={notice.type === 'error' ? 'error' : 'success'}
              icon={notice.type === 'error' ? AlertTriangle : CheckCircle2}
            >
              {notice.text}
            </Alert>
          </button>
        )}

        {loading ? (
          <Card variant="glass" padded className="mt-6"><p className="text-sm text-ink-muted">Chargement…</p></Card>
        ) : items.length === 0 ? (
          <Card variant="glass" padded className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-r-lg bg-grc-tint/70 text-grc-fg">
                <Vote className="h-5 w-5" />
              </div>
              <div>
                <div className="font-medium text-ink">Aucune consultation en cours</div>
                <div className="text-sm text-ink-muted">Vous serez sollicité·e dès qu'une consultation sera ouverte.</div>
              </div>
            </div>
          </Card>
        ) : (
          <div className="mt-6 space-y-8">
            {ouvertes.length > 0 && (
              <section aria-labelledby="open-title">
                <h2 id="open-title" className="font-display text-base font-semibold text-ink mb-3">À voter</h2>
                <div className="space-y-4">
                  {ouvertes.map((r) => (
                    <ReferendumVoteCard key={r.id} referendum={r} onVoted={reload} onError={setError} bloque={bloques.has(r.id)} />
                  ))}
                </div>
              </section>
            )}

            {closes.length > 0 && (
              <section aria-labelledby="closed-title">
                <h2 id="closed-title" className="font-display text-base font-semibold text-ink mb-3">Résultats</h2>
                <div className="space-y-4">
                  {closes.map((r) => (
                    <ReferendumResultatCard key={r.id} referendum={r} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* ===== Boîte à idées (migration 130) ===== */}
        <section className="mt-10">
          <SectionEyebrow as="div">Participation</SectionEyebrow>
          <h2 className="mt-1.5 font-display text-lg font-semibold tracking-r-tight text-ink sm:text-xl">
            Boîte à idées
          </h2>
          <div className="mt-4">
            <IdeesSection />
          </div>
        </section>
      </main>
    </div>
  );
}

// ============================================================================
// Carte vote (consultation ouverte)
// ============================================================================

function ReferendumVoteCard({
  referendum: r, onVoted, onError, bloque = false,
}: {
  referendum: Referendum;
  onVoted: () => void;
  onError: (m: string) => void;
  bloque?: boolean;
}) {
  const [selected, setSelected] = useState<string | null>(r.monVoteOptionId ?? null);
  const [busy, setBusy] = useState(false);
  const aVote = !!r.monVoteOptionId;
  const officiel = r.mode === 'officiel';
  const fcVerifie = r.fcVerifie ?? !officiel; // indicatif => pas de vérif requise

  async function verifierIdentite() {
    setBusy(true);
    try {
      const { url } = await api.referendumsCitoyen.franceConnectLogin(r.id);
      window.location.href = url; // redirection vers FranceConnect
    } catch (e) {
      onError(e instanceof ApiException ? e.message : 'Connexion FranceConnect impossible');
      setBusy(false);
    }
  }

  async function submit() {
    if (!selected) return;
    setBusy(true);
    try {
      await api.referendumsCitoyen.vote(r.id, selected);
      onVoted();
    } catch (e) {
      onError(e instanceof ApiException ? e.message : 'Vote impossible');
    } finally { setBusy(false); }
  }

  return (
    <Card variant="glass" padded>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="success" size="sm">Ouverte</Badge>
        <span className="ml-auto text-xs text-ink-dim tabular-nums">Clôture {fmtDate(r.clotureAt)}</span>
      </div>
      <h3 className="mt-2 font-display text-base font-semibold text-ink">{r.question}</h3>
      {r.description && <p className="mt-1 text-sm text-ink-muted">{r.description}</p>}

      {/* Options en radio (pas de résultats : secret du scrutin) */}
      <div className="mt-3 space-y-2">
        {r.options.map((o) => {
          const isSel = selected === o.id;
          return (
            <button
              key={o.id}
              type="button"
              disabled={bloque}
              onClick={() => { if (!bloque) setSelected(o.id); }}
              className={[
                'flex w-full items-center gap-3 rounded-r-lg border px-3.5 py-3 text-left transition-all',
                bloque ? 'cursor-not-allowed opacity-50' : '',
                isSel ? 'border-grc bg-grc-tint/40' : 'border-border bg-surface/50 hover:border-border-strong',
              ].join(' ')}
            >
              <span className={[
                'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2',
                isSel ? 'border-grc' : 'border-border-strong',
              ].join(' ')}>
                {isSel && <span className="h-2 w-2 rounded-full bg-grc" />}
              </span>
              <span className="text-sm text-ink">{o.libelle}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-3">
        {officiel && bloque ? (
          <span className="inline-flex items-center gap-2 rounded-r-lg border border-urgent/30 bg-urgent-tint/40 px-3.5 py-2 text-sm font-medium text-urgent">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            Cette identité FranceConnect a déjà voté — vote impossible
          </span>
        ) : officiel && !fcVerifie ? (
          <Button variant="primary" onClick={verifierIdentite} disabled={busy}>
            <ShieldCheck className="h-4 w-4" /> {busy ? 'Redirection…' : 'Vérifier mon identité FranceConnect'}
          </Button>
        ) : officiel ? (
          <Button variant="primary" onClick={submit} disabled={!selected || busy}>
            <ShieldCheck className="h-4 w-4" /> {busy ? 'Envoi…' : aVote ? 'Modifier mon vote' : 'Voter (identité vérifiée)'}
          </Button>
        ) : (
          <Button variant="primary" onClick={submit} disabled={!selected || busy}>
            <Vote className="h-4 w-4" /> {busy ? 'Envoi…' : aVote ? 'Modifier mon vote' : 'Valider mon vote'}
          </Button>
        )}
        {officiel && fcVerifie && !aVote && (
          <span className="inline-flex items-center gap-1.5 text-sm text-success">
            <CheckCircle2 className="h-4 w-4" /> Identité vérifiée
          </span>
        )}
        {aVote && (
          <span className="inline-flex items-center gap-1.5 text-sm text-success">
            <CheckCircle2 className="h-4 w-4" /> Vous avez voté
          </span>
        )}
      </div>

      {/* Statut du vote personnel : résultats masqués + fenêtre de modif */}
      {aVote && (
        <div className="mt-3 flex items-start gap-2 rounded-r-lg border border-border bg-bg/50 p-3 text-sm text-ink-muted">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-dim" />
          <div>
            Votre vote est enregistré. Les résultats seront publiés à la clôture.
            {r.monVoteModifiable
              ? ' Vous pouvez encore le corriger pendant 1 heure après l\'avoir émis.'
              : ' Votre vote est désormais définitif.'}
          </div>
        </div>
      )}

      {/* Bandeau indicatif vs authentifié */}
      {officiel ? (
        <div className="mt-3 flex items-start gap-2 rounded-r-lg border border-grc/30 bg-grc-tint/40 p-3 text-sm text-grc-fg">
          <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div><b>Scrutin authentifié.</b> Votre identité garantit l'unicité et la valeur officielle du résultat.</div>
        </div>
      ) : (
        <div className="mt-3 flex items-start gap-2 rounded-r-lg border border-warning/30 bg-warning-tint/50 p-3 text-sm text-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div><b>Consultation indicative.</b> Le résultat éclaire la décision municipale mais n'a pas de valeur officielle.</div>
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// Carte résultats (consultation clôturée — visible par tous)
// ============================================================================

function ReferendumResultatCard({ referendum: r }: { referendum: Referendum }) {
  return (
    <Card variant="glass" padded>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="neutral" size="sm">Clôturée</Badge>
        <span className="ml-auto text-xs text-ink-dim tabular-nums">
          {r.totalVotants} votant{r.totalVotants > 1 ? 's' : ''}
        </span>
      </div>
      <h3 className="mt-2 font-display text-base font-semibold text-ink">{r.question}</h3>

      <div className="mt-3 space-y-2">
        {r.options.map((o) => {
          const voix = o.voix ?? 0;
          const pct = r.totalVotants > 0 ? Math.round((voix / r.totalVotants) * 100) : 0;
          const gagnant = r.monVoteOptionId === o.id;
          return (
            <div key={o.id} className="flex items-center gap-3">
              <span className="w-40 shrink-0 truncate text-sm text-ink">
                {o.libelle}{gagnant && <span className="ml-1 text-xs text-grc-fg">(votre choix)</span>}
              </span>
              <div className="relative h-2.5 flex-1 overflow-hidden rounded-r-pill bg-bg-2">
                <div className="absolute inset-y-0 left-0 rounded-r-pill bg-grc" style={{ width: `${pct}%` }} />
              </div>
              <span className="w-16 text-right text-sm font-medium text-grc-fg tabular-nums">{pct}%</span>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-ink-dim">Résultats définitifs, visibles par tous depuis la clôture.</p>
    </Card>
  );
}
